import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { outreachApiConfig } from "../config/api.config";
import { resubscribeContact } from "../services/subscriptionService";

type RequestStatus = "idle" | "loading" | "success" | "error";

const DEFAULT_TOPIC_LABEL = "our email list";

const UnsubscribeConfirmation: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [requestStatus, setRequestStatus] = useState<RequestStatus>("idle");
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const email = searchParams.get("email")?.trim() ?? "";
  const topic = searchParams.get("topic")?.trim() ?? "";
  const topicLabel = topic || DEFAULT_TOPIC_LABEL;
  const hasEmail = Boolean(email);

  const confirmationDescription = hasEmail
    ? `We have unsubscribed ${email} from ${topicLabel}.`
    : "We could not detect which email unsubscribed. If this was a mistake, please contact support.";

  const managePreferencesUrl = outreachApiConfig.managePreferencesUrl;
  const isExternalManageLink = /^https?:\/\//i.test(managePreferencesUrl);

  const handleResubscribe = useCallback(async () => {
    if (!hasEmail) {
      setErrorMessage(
        "We need to know which email to re-subscribe. Please use the original link or contact support."
      );
      return;
    }

    setRequestStatus("loading");
    setErrorMessage(null);
    setFeedbackMessage(null);

    try {
      const result = await resubscribeContact(email, topic || undefined);
      setRequestStatus("success");
      setFeedbackMessage(result.message || "You have been re-subscribed.");
    } catch (error) {
      setRequestStatus("error");
      const message =
        error instanceof Error
          ? error.message
          : "Unable to reach the subscription service.";
      setErrorMessage(message);
    }
  }, [email, hasEmail, topic]);

  const managePreferencesLink = useMemo(() => {
    const linkContent = (
      <Typography
        component="span"
        variant="body2"
        color="primary"
        sx={{ fontWeight: 500 }}
      >
        Manage your subscription preferences
      </Typography>
    );

    if (isExternalManageLink) {
      return (
        <MuiLink
          href={managePreferencesUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          {linkContent}
        </MuiLink>
      );
    }

    return (
      <MuiLink component={RouterLink} to={managePreferencesUrl} underline="hover">
        {linkContent}
      </MuiLink>
    );
  }, [isExternalManageLink, managePreferencesUrl]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 6, md: 10 },
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 3,
          p: { xs: 3, md: 5 },
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="overline" color="text.secondary">
              Subscription update
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {hasEmail ? "You are unsubscribed" : "Subscription update received"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {confirmationDescription}
            </Typography>
          </Stack>

          <Stack
            spacing={1}
            sx={{
              backgroundColor: theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Topic
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {topicLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Email address
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {hasEmail ? email : "Unknown email"}
            </Typography>
          </Stack>

          <Stack spacing={2}>
            {errorMessage && (
              <Alert severity="error" role="alert">
                {errorMessage}
              </Alert>
            )}
            {feedbackMessage && (
              <Alert severity="success">{feedbackMessage}</Alert>
            )}

            {!hasEmail && (
              <Alert severity="warning">
                Please double-check the unsubscribe link from the email you received.
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleResubscribe}
              disabled={requestStatus === "loading" || !hasEmail}
            >
              {requestStatus === "loading" ? "Processing..." : "Re-subscribe"}
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Want full control over what you receive? {managePreferencesLink}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default UnsubscribeConfirmation;
